import { createContext, useCallback, useContext, useRef, useState, type ReactNode, type SetStateAction } from 'react';
import { getDefaultColumnConfig, resolveViewMode, BROWSER_TRACKER_UI_CAPABILITIES, type SavedViewDefinition, type TrackerViewMode } from '@nimbalyst/collab-bundle/trackers-ui';

export function initialTrackerBrowseState(definition: SavedViewDefinition) {
  return {
    searchTerm: '',
    viewMode: resolveViewMode(definition.viewMode, BROWSER_TRACKER_UI_CAPABILITIES).mode,
    phoneViewMode: null as TrackerViewMode | null,
    groupBy: definition.groupBy,
    ordering: definition.ordering,
    columnConfig: definition.columnConfig ?? getDefaultColumnConfig(definition.selectedType === 'all' ? '' : definition.selectedType),
    columnFilters: definition.columnFilters,
    statusScope: definition.statusScope,
  };
}
export type TrackerBrowseState = ReturnType<typeof initialTrackerBrowseState>;
export interface BrowseEntry { state: TrackerBrowseState; scrollTop: number }
interface BrowseCache { views: Map<string, BrowseEntry>; drafts: Map<string, string> }
const BrowseContext = createContext<BrowseCache | null>(null);

/** Owned by the readable project session; revocation/unmount drops cached content. */
export function TrackerBrowseStateProvider({ children }: { children: ReactNode }) {
  const [cache] = useState<BrowseCache>(() => ({ views: new Map(), drafts: new Map() }));
  return <BrowseContext.Provider value={cache}>{children}</BrowseContext.Provider>;
}

export function phonePresentation(state: TrackerBrowseState, phone: boolean): TrackerViewMode {
  return phone ? state.phoneViewMode ?? 'list' : state.viewMode;
}

export function useTrackerBrowseState(viewId: string | null, definition: SavedViewDefinition) {
  const cache = useContext(BrowseContext);
  const fallback = useRef(new Map<string, BrowseEntry>());
  const views = cache?.views ?? fallback.current;
  // Include the authoritative definition: a changed shared view must not reuse stale filters.
  const key = JSON.stringify([viewId, definition]);
  if (!views.has(key)) views.set(key, { state: initialTrackerBrowseState(definition), scrollTop: 0 });
  const entry = views.get(key)!;
  const [, render] = useState(0);
  const setState = useCallback((update: SetStateAction<TrackerBrowseState>) => {
    entry.state = typeof update === 'function' ? update(entry.state) : update;
    render(value => value + 1);
  }, [entry]);
  return { viewState: entry.state, setViewState: setState, browseEntry: entry, browseKey: key };
}

export function useTrackerCommentDraft(itemId: string) {
  const cache = useContext(BrowseContext);
  const [local, setLocal] = useState(() => ({ itemId, text: cache?.drafts.get(itemId) ?? '' }));
  const text = local.itemId === itemId ? local.text : cache?.drafts.get(itemId) ?? '';
  const setText = (value: string) => {
    if (value) cache?.drafts.set(itemId, value);
    else cache?.drafts.delete(itemId);
    setLocal({ itemId, text: value });
  };
  return [text, setText] as const;
}
